import { logger, AnalyticsModuleBootstrapError } from "../../debug";
import { getRegion, isStaging } from "../../generalUtils/envFns";
import type { EventHandlerPayload } from "../../automated/types";
import { emitDatadogAction } from "./utils";

interface DatadogActionContext extends Record<string, unknown> {
  automatedEventName: string;
  validationSuccess: boolean | null;
}

export interface DatadogPluginConfig {
  enabled?: boolean;
  debug?: boolean;
  globalContext?: Record<string, unknown>;
}

type DatadogEventPayload = Pick<EventHandlerPayload["payload"], "event">;

type DatadogEventProperties = {
  payload?: DatadogEventPayload;
  validationResult?: {
    success?: boolean;
    data?: {
      payload?: DatadogEventPayload;
    };
  };
};

const DATADOG_ACTION_NAME = "analytics_automated_event";
const DATADOG_GLOBAL_CONTEXT_KEY = "lll_analytics_automated_bootstrap";

type DatadogTrackEndPayload = {
  event?: string;
  properties?: DatadogEventProperties;
};

export default (config: DatadogPluginConfig) => {
  function buildDatadogActionContext(
    properties: DatadogEventProperties | undefined
  ): DatadogActionContext {
    const validationSuccess = properties?.validationResult?.success ?? null;

    const automatedEventName =
      [
        properties?.validationResult?.data?.payload?.event?.eventName,
        properties?.payload?.event?.eventName,
      ].find(
        (eventName): eventName is string =>
          typeof eventName === "string" && eventName.trim().length > 0
      ) ?? "unknown";

    return {
      automatedEventName,
      validationSuccess,
    };
  }

  function setDatadogGlobalContext(): void {
    const rumClient = window.DD_RUM;

    if (
      !rumClient ||
      typeof rumClient.setGlobalContextProperty !== "function"
    ) {
      return;
    }

    const setRegionContext = () => {
      rumClient.setGlobalContextProperty(DATADOG_GLOBAL_CONTEXT_KEY, {
        ...(config.globalContext ?? {}),
        region: getRegion(),
        isStaging: isStaging(),
      });
    };

    if (typeof rumClient.onReady === "function") {
      rumClient.onReady(setRegionContext);
      return;
    }

    setRegionContext();
  }

  function sendDatadogAction(context: DatadogActionContext): void {
    emitDatadogAction(DATADOG_ACTION_NAME, context);
  }

  return {
    name: "datadog-plugin",
    config,
    enabled: config.enabled ?? false,
    loaded() {
      return typeof window !== "undefined" && window.DD_RUM !== undefined;
    },
    initialize() {
      logger.debug("Datadog Plugin is loading");
      setDatadogGlobalContext();
    },
    trackEnd: ({ payload }: { payload?: DatadogTrackEndPayload }) => {
      if (payload?.event !== "automatedData") {
        return;
      }

      try {
        const actionContext = buildDatadogActionContext(payload?.properties);

        sendDatadogAction(actionContext);
      } catch (error) {
        logger.error(
          new AnalyticsModuleBootstrapError(
            "Datadog plugin failed to emit analytics action",
            {
              cause: error,
            }
          )
        );
      }
    },
  };
};
